import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';

/**
 * SectionHeader - A consistent section header component used across all modules
 * Matches the Project Summary header style with gradient background
 * 
 * @param {string} title - The section title
 * @param {React.ReactNode} icon - The icon component to display
 * @param {string} iconColor - The color class for the icon (e.g., 'text-purple-500')
 * @param {React.ReactNode} actions - Action buttons to display on the right
 * @param {React.ReactNode} badge - Optional badge to display next to title
 * @param {React.ReactNode} customTitle - Custom title JSX (overrides title, icon, and badge)
 * @param {string} subtitle - Optional subtitle text below title
 * @param {React.ReactNode} children - The section content
 * @param {string} className - Additional classes for the card
 * @param {string} gradient - Custom gradient classes (default: rainbow gradient)
 */
const SectionHeader = ({ 
  title, 
  icon: Icon, 
  iconColor = 'text-purple-500',
  actions,
  badge,
  customTitle,
  subtitle,
  children,
  className = '',
  gradient = 'from-purple-500/10 via-pink-500/10 to-blue-500/10'
}) => {
  return (
    <Card className={`bg-card border-border overflow-hidden ${className}`}>
      <CardHeader className={`bg-gradient-to-r ${gradient} border-b border-border py-4`}>
        <div className="flex items-center justify-between">
          {customTitle ? (
            <CardTitle className="flex items-center gap-2 text-foreground text-lg">
              {customTitle}
            </CardTitle>
          ) : (
            <CardTitle className="flex items-center gap-2 text-foreground text-lg">
              {Icon && <Icon className={`w-5 h-5 ${iconColor}`} />}
              <div>
                <span>{title}</span>
                {subtitle && <p className="text-xs font-normal text-muted-foreground">{subtitle}</p>}
              </div>
              {badge}
            </CardTitle>
          )}
          {actions && (
            <div className="flex items-center gap-2">
              {actions}
            </div>
          )}
        </div>
      </CardHeader>
      {children && (
        <CardContent className="pt-6 pb-6">
          {children}
        </CardContent>
      )}
    </Card>
  );
};

export default SectionHeader;
